import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { IFRAME_DESIGN_CSS, CONTRAST_INSURANCE_JS } from './iframeDesignSystem';

/**
 * PersistentHtmlRenderer — single pre-warmed iframe per session lifetime.
 *
 * Instead of creating a new iframe + loading Tailwind CDN for every HTML block,
 * this component maintains ONE iframe that:
 *   1. Pre-loads Tailwind CDN once on mount
 *   2. Receives HTML chunks via postMessage (O(1) append, no re-parse)
 *   3. Reports height changes via ResizeObserver
 *   4. Forwards data-action button clicks to parent
 *
 * Props:
 *   streamingChunks: string[]  — cumulative array of HTML chunks
 *   isStreaming: boolean       — whether stream is active
 *   onAction: (data) => void   — callback for data-action button clicks
 *   className: string          — wrapper className
 */

// The iframe srcdoc — loaded once, never recreated.
// Contains: Tailwind CDN, bridge script, ResizeObserver, action bridge.
const IFRAME_SRCDOC = `<!DOCTYPE html>
<html style="color-scheme:light">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">
  <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
  <style>${IFRAME_DESIGN_CSS}
    #streaming-indicator {
      position: sticky;
      top: 0;
      z-index: 10;
      height: 2px;
      background: linear-gradient(90deg, transparent, #a855f7, transparent);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite linear;
      display: none;
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  </style>
  <style id="extra-css"></style>
  <script>
    ${CONTRAST_INSURANCE_JS}
    var lastSentChunkCount = 0;
    var lastSentHeight = 0;
    // Convergence guard: if height grows monotonically in tiny increments
    // across consecutive samples without a semantic DOM change, freeze.
    // Signature of the vh/min-h-screen feedback loop, not real content growth.
    var heightSamples = [];
    var HEIGHT_GUARD_SAMPLES = 4;
    var HEIGHT_GUARD_EPSILON = 64; // px of total drift tolerated before freeze
    var frozenHeight = 0;

    function resetHeightGuard() {
      heightSamples = [];
      frozenHeight = 0;
    }

    function notifyHeight() {
      var root = document.getElementById('root');
      if (!root) return;
      var h = root.scrollHeight;
      if (h === lastSentHeight) return;
      // If guard has frozen, only allow height to shrink or to exceed frozen by a large step
      if (frozenHeight && h > frozenHeight && h - frozenHeight < HEIGHT_GUARD_EPSILON * 2) {
        return;
      }
      heightSamples.push(h);
      if (heightSamples.length > HEIGHT_GUARD_SAMPLES) heightSamples.shift();
      if (heightSamples.length === HEIGHT_GUARD_SAMPLES) {
        var monotonic = true;
        for (var i = 1; i < heightSamples.length; i++) {
          var delta = heightSamples[i] - heightSamples[i - 1];
          if (delta <= 0) { monotonic = false; break; }
        }
        var totalDrift = heightSamples[heightSamples.length - 1] - heightSamples[0];
        if (monotonic && totalDrift > 0 && totalDrift < HEIGHT_GUARD_EPSILON) {
          frozenHeight = heightSamples[0];
          lastSentHeight = frozenHeight;
          parent.postMessage({ type: 'vi_resize', height: frozenHeight }, '*');
          return;
        }
      }
      lastSentHeight = h;
      parent.postMessage({ type: 'vi_resize', height: h }, '*');
    }

    // Full-document detection: chunk starts (after whitespace/BOM) with
    // <!doctype or <html — these are invalid as children of a <div> and also
    // block script execution when inserted via insertAdjacentHTML.
    function isFullDocumentHtml(html) {
      if (typeof html !== 'string') return false;
      var trimmed = html.replace(/^[\\s\\uFEFF]+/, '');
      return /^<!doctype/i.test(trimmed) || /^<html[\\s>]/i.test(trimmed);
    }

    // Rehydrate <script> tags by cloning into fresh elements — the only way
    // browsers execute scripts that were inserted dynamically.
    function rehydrateScripts(container) {
      var scripts = container.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var old = scripts[i];
        var fresh = document.createElement('script');
        for (var j = 0; j < old.attributes.length; j++) {
          var a = old.attributes[j];
          fresh.setAttribute(a.name, a.value);
        }
        if (old.textContent) fresh.textContent = old.textContent;
        old.parentNode.replaceChild(fresh, old);
      }
    }

    // Append a full HTML document: parse, lift head styles into our head,
    // move body children into #root, then rehydrate scripts so they execute.
    function appendFullDocument(html) {
      var doc;
      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (err) {
        // Fallback to the legacy path if the parser is unavailable
        document.getElementById('root').insertAdjacentHTML('beforeend', html);
        return;
      }
      var root = document.getElementById('root');
      var headNodes = doc.head ? doc.head.querySelectorAll('style, link[rel="stylesheet"]') : [];
      for (var i = 0; i < headNodes.length; i++) {
        document.head.appendChild(headNodes[i].cloneNode(true));
      }
      if (doc.body) {
        while (doc.body.firstChild) {
          root.appendChild(doc.body.firstChild);
        }
      }
      rehydrateScripts(root);
    }

    // PostMessage Bridge
    window.addEventListener('message', function(e) {
      var d = e.data;
      if (!d || !d.type) return;

      if (d.type === 'append') {
        if (isFullDocumentHtml(d.html)) {
          appendFullDocument(d.html);
        } else {
          document.getElementById('root').insertAdjacentHTML('beforeend', d.html);
        }
        resetHeightGuard();
        notifyHeight();
      } else if (d.type === 'render_full') {
        // TRUE token-streaming (#2795 S1): the publish_card html streams in tiny
        // chunks that split mid-tag, so appending suffixes via insertAdjacentHTML
        // garbles the markup. Instead re-parse the FULL accumulated html each
        // (throttled) update — the HTML parser tolerates an incomplete trailing
        // tag, so partial frames render cleanly. innerHTML does NOT run <script>,
        // which is what we want mid-stream; on the final frame we rehydrate them.
        var rroot = document.getElementById('root');
        if (rroot) {
          if (isFullDocumentHtml(d.html)) {
            rroot.innerHTML = '';
            appendFullDocument(d.html);
          } else {
            rroot.innerHTML = d.html;
            if (d.final) rehydrateScripts(rroot);
          }
          resetHeightGuard();
          notifyHeight();
        }
      } else if (d.type === 'reset') {
        document.getElementById('root').innerHTML = '';
        lastSentChunkCount = 0;
        resetHeightGuard();
        notifyHeight();
      } else if (d.type === 'stream_start') {
        document.getElementById('streaming-indicator').style.display = 'block';
      } else if (d.type === 'stream_end') {
        document.getElementById('streaming-indicator').style.display = 'none';
        notifyHeight();
        _viScheduleContrast();
      } else if (d.type === 'styles') {
        document.getElementById('extra-css').textContent = d.css || '';
      } else if (d.type === 'vi_toggle' && d.target) {
        var el = document.getElementById(d.target);
        if (el) el.classList.toggle('hidden');
      }
    });

    // ResizeObserver for auto height — observe #root, NOT documentElement
    // (observing documentElement causes feedback loops when parent resizes iframe)
    var ro = new ResizeObserver(function() { notifyHeight(); });
    // Defer to ensure #root exists. The iframe srcdoc occasionally reloads
    // (React key / srcdoc churn) and on fast re-loads getElementById('root')
    // can still return null when the timeout fires — guard so we don't pass
    // null to ResizeObserver.observe (which throws TypeError).
    function attachRootObserver() {
      var el = document.getElementById('root');
      if (el) { ro.observe(el); return; }
      setTimeout(attachRootObserver, 16);
    }
    setTimeout(attachRootObserver, 0);

    // Clipboard helper — copy_text is performed IN the iframe so it works on every
    // route that renders this iframe (playground AND the public /share view, which
    // has no parent action handler). This is the app-supported primitive that lets
    // agents declare a copy button WITHOUT hand-rolling inline onclick JS.
    function viCopyToClipboard(text) {
      if (text == null) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).catch(function() { viCopyFallback(text); });
          return;
        }
      } catch (_) {}
      viCopyFallback(text);
    }
    function viCopyFallback(text) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) {}
    }

    // Action Bridge — declarative data-action buttons (NO inline JS in agent HTML).
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      e.preventDefault();
      var action = btn.dataset.action;
      var payload = { type: 'vi_action', action: action };
      for (var key in btn.dataset) {
        if (key !== 'action') payload[key] = btn.dataset[key];
      }
      // copy_text: copy data-text, or the innerText of data-copy-target (quote-proof —
      // reads the displayed text from a node instead of inlining it into an attribute).
      if (action === 'copy_text') {
        var text = btn.dataset.text;
        if (text == null && btn.dataset.copyTarget) {
          var src = document.getElementById(btn.dataset.copyTarget);
          text = src ? (src.innerText || src.textContent || '') : '';
        }
        viCopyToClipboard(text || '');
      }
      // open_map: open the maps URL directly (works on both web surfaces without a
      // parent onAction — the app intercepts this same action via its native bridge instead).
      if (action === 'open_map' && btn.dataset.url) {
        try { window.open(btn.dataset.url, '_blank', 'noopener,noreferrer'); } catch (_) {}
      }
      parent.postMessage(payload, '*');
      var original = btn.innerHTML;
      btn.innerHTML = action === 'copy_text' ? '\\u2713 Copied' : '\\u2713';
      btn.style.opacity = '0.7';
      setTimeout(function() { btn.innerHTML = original; btn.style.opacity = '1'; }, 1500);
    });

    // Signal ready
    parent.postMessage({ type: 'vi_ready' }, '*');
  </script>
</head>
<body>
  <div id="streaming-indicator"></div>
  <div id="root"></div>
</body>
</html>`;

export default function PersistentHtmlRenderer({ streamingChunks, isStreaming, onAction, className }) {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(100);
  const [iframeReady, setIframeReady] = useState(false);
  // #2795 S1: we re-render the FULL accumulated html each (throttled) update
  // rather than appending suffixes — token-level chunks split mid-tag, and
  // insertAdjacentHTML can't stitch those, so it garbled the markup. We keep the
  // latest full html here and coalesce rapid token updates to ≤1 re-parse/frame.
  const prevStreamingRef = useRef(false);
  const pendingFullRef = useRef(null);
  const rafRef = useRef(null);
  // Restored: the #2844 v080 back-merge dropped these two declarations while the
  // usages in reset()/the empty-chunk effect below survived — leaving them
  // `no-undef` (a ReferenceError if reset() ever runs). They track streamed-chunk
  // bookkeeping for the reset-on-new-block-cycle guard.
  const sentChunkLengthsRef = useRef([]);
  const pendingChunksRef = useRef([]);

  // Listen for iframe messages (resize, action, ready)
  useEffect(() => {
    const handler = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const data = e.data;
      if (!data || !data.type) return;

      if (data.type === 'vi_resize') {
        const newHeight = Math.max(data.height, 100);
        setIframeHeight(newHeight);
      } else if (data.type === 'vi_action') {
        onAction?.(data);
      } else if (data.type === 'vi_ready') {
        setIframeReady(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onAction]);

  // Helper to post a message to the iframe
  const postToIframe = useCallback((msg) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(msg, '*');
    }
  }, []);

  // Render the FULL accumulated html on each update (re-parse, tolerant of an
  // incomplete trailing tag) instead of appending mid-tag suffixes. While
  // streaming we coalesce to ≤1 re-parse per animation frame (token bursts are
  // cheap); when not streaming (stream ended, or a GET-reconstructed card) we
  // render once immediately and mark it `final` so embedded scripts execute.
  // Re-runs when the iframe becomes ready, flushing whatever arrived early.
  useEffect(() => {
    if (!iframeReady || !streamingChunks || streamingChunks.length === 0) return;
    const fullHtml = streamingChunks.join('');
    pendingFullRef.current = fullHtml;

    if (!isStreaming) {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      postToIframe({ type: 'render_full', html: fullHtml, final: true });
      return;
    }
    if (rafRef.current != null) return; // a re-parse is already scheduled this frame
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      postToIframe({ type: 'render_full', html: pendingFullRef.current, final: false });
    });
  }, [streamingChunks, isStreaming, iframeReady, postToIframe]);

  // Handle streaming state transitions
  useEffect(() => {
    if (!iframeReady) return;

    if (isStreaming && !prevStreamingRef.current) {
      postToIframe({ type: 'stream_start' });
    } else if (!isStreaming && prevStreamingRef.current) {
      postToIframe({ type: 'stream_end' });
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming, iframeReady, postToIframe]);

  // Reset method exposed via ref if needed — also callable internally
  const reset = useCallback(() => {
    sentChunkLengthsRef.current = [];
    pendingChunksRef.current = [];
    if (iframeReady) {
      postToIframe({ type: 'reset' });
    }
  }, [iframeReady, postToIframe]);

  // Reset when chunks array is emptied (new block cycle)
  useEffect(() => {
    if (streamingChunks && streamingChunks.length === 0 && sentChunkLengthsRef.current.length > 0) {
      reset();
    }
  }, [streamingChunks, reset]);

  // Height transition — smooth only when NOT streaming
  const heightStyle = useMemo(() => ({
    height: `${iframeHeight}px`,
    border: 'none',
    transition: isStreaming ? 'none' : 'height 0.2s ease',
  }), [iframeHeight, isStreaming]);

  return (
    <div className={className}>
      <iframe
        ref={iframeRef}
        srcDoc={IFRAME_SRCDOC}
        className="w-full bg-transparent"
        style={heightStyle}
        // Issue #1411 follow-up: ``allow-same-origin`` is required so that
        // ``<img src="/api/media/...">`` requests from within this iframe
        // inherit the parent's cookies. Without it the iframe runs at a
        // null opaque origin, every ``/api/media`` fetch is credential-less,
        // and the owner-auth proxy returns 401 (images silently break).
        // Card HTML is server-authored / templated — trusted — so relaxing
        // the sandbox here is safe.
        sandbox="allow-scripts allow-popups allow-same-origin"
        title="Agent content"
      />
    </div>
  );
}
